/* eslint-disable @typescript-eslint/no-explicit-any */

import config from 'src/config'
import { isAdvertisingRejected } from 'src/utils/storedCookieConsent'
import { isInternalTraffic } from './internal'

// eslint-disable-next-line no-console
const dummy = () => console.info

let baseInitDone = false

/**
 * `fbq`, installed on first use as Meta's base-code stub: it queues every call
 * until fbevents.js — loaded later by <Scripts /> on a 2 s fuse — replays the
 * queue in order. Same queue-first pattern as getHsq() / getUetq(). Until
 * 2026-09-23 `fbq` only appeared once the delayed loader ran, so every earlier
 * call hit the no-op: the landing PageView, early ViewContent / Search /
 * identify, and CookieConsent's re-applied consent (a visitor who had rejected
 * advertising cookies was still tracked by Meta on the next page load).
 *
 * The one base `init` applies the stored cookie choice first — a revoke must
 * precede `init` to hold.
 */
const getFbp = (): any => {
  if (typeof window === 'undefined') return dummy
  const w = window as any
  if (typeof w.fbq !== 'function') {
    const stub: any = function (...args: unknown[]) {
      if (stub.callMethod) stub.callMethod.apply(stub, args)
      else stub.queue.push(args)
    }
    stub.push = stub
    stub.loaded = true
    stub.version = '2.0'
    stub.queue = []
    if (!w._fbq) w._fbq = stub
    w.fbq = stub
  }
  if (!baseInitDone) {
    baseInitDone = true
    // No automatic PageView from Meta's history.pushState/replaceState hook
    // (2026-10-26). <Scripts /> already sends one on every routeChangeComplete,
    // so Meta counted each SPA URL change twice, and the hook's work ran inside
    // the tap on every search card (its history-only replace), ~156 ms of
    // Android INP with Bing/LinkedIn (docs/performance/interaction-latency.md).
    // Must be set before fbevents.js loads — it reads the flag at init.
    w.fbq.disablePushState = true
    if (isAdvertisingRejected()) w.fbq('consent', 'revoke')
    w.fbq('init', config.pixels.fb)
  }
  return w.fbq
}

/** Installs the stub + base init before fbevents.js loads (see getFbp). */
const install = (): void => {
  getFbp()
}

export const pageview = () => {
  if (isInternalTraffic()) return
  const fPixel = getFbp()
  fPixel('track', 'PageView')
}

type IFacebookEvents =
  | 'AddPaymentInfo'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'InitiateCheckout'
  | 'Lead'
  | 'PageView'
  | 'Purchase'
  | 'Schedule'
  | 'Search'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe'
  | 'ViewContent'

export const event = (name: IFacebookEvents, options = {}) => {
  if (isInternalTraffic()) return
  const fPixel = getFbp()
  fPixel('track', name, options)
}

export const trackCustom = (name: string, options = {}) => {
  if (isInternalTraffic()) return
  const fPixel = getFbp()
  fPixel('trackCustom', name, options)
}

const identifyUser = (user: {
  email?: string | undefined | null
  phone?: string | undefined | null
  fName?: string | undefined | null
  lName?: string | undefined | null
  address?: {
    city?: string | undefined | null
    state?: string | undefined | null
    zipcode?: string
    country?: string | undefined | null
  }
}) => {
  // Advanced matching hands Meta the email / phone / name — never after the
  // visitor turned advertising off, even though the revoke would hold it.
  if (isInternalTraffic() || isAdvertisingRejected()) return
  const fPixel = getFbp()
  if (fPixel && user.email) {
    fPixel('init', config.pixels.fb, {
      em: user.email,
      ph: user.phone,
      fn: user.fName,
      ln: user.lName,
      ct: user.address?.city,
      st: user.address?.state,
      zp: user.address?.zipcode,
      country: user.address?.country
    })
  }
}

/**
 * Meta consent. Not persisted by the pixel — CookieConsent re-applies it on
 * every load from the stored choice (getFbp's base init already applied it
 * before `init`; this also covers a choice made on the banner). Queued like any
 * other call when fbevents.js has not loaded yet.
 */
const setConsent = (advertisementAllowed: boolean): void => {
  getFbp()('consent', advertisementAllowed ? 'grant' : 'revoke')
}

const fbp = { install, pageview, event, trackCustom, identifyUser, setConsent }

export default fbp
